
import { defineComponent, onMounted, reactive } from 'vue'

// @ui-kit
import NewSelect from '@/ui-kit/NewSelect/index.vue'
import NewButton from '@/ui-kit/NewButton/index.vue'
import NewInput from '@/ui-kit/NewInput/index.vue'
import NewCheckbox from '@/common/new-components/NewCheckbox/index.vue'

// @types
import { CityT } from '@/core/types/cities.types'
import { CommentTMS, TransportTypeTMS, TripStatusTMS } from '@/new-services/types/tms'

type Period = {
    id: string
    name: string
}

// @new-services
import locationAPI from '@/new-services/location.service'
import tmsAPI from '@/new-services/tms.service'
import { useDate } from '@/common/composable/useDate'

const STATUSES_EXCLUDED = [1, 4]
const PERIODS = [
    { id: 'today', name: 'Сегодня' },
    { id: 'yesterday', name: 'Вчера' },
    { id: 'this_week', name: 'Текущая неделя' },
    { id: 'previous_week', name: 'Прошлая неделя' },
    { id: 'this_month', name: 'Текущий месяц' },
] as Period[]

export default defineComponent({
    components: {
        NewSelect,
        NewButton,
        NewInput,
        NewCheckbox,
    },
    props: {
        loading: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['submit', 'reset'],
    setup(props, { emit }) {
        const { getToday, getYesterday, getThisWeek, getPreviousWeek, getThisMonth } = useDate()

        const state = reactive({
            ttnNumber: null as string | null,
            tripNumber: null as string | null,
            fromCityId: null as number | null,
            toCityId: null as number | null,
            dateFrom: null as string | null,
            dateTo: null as string | null,
            //
            transportTypeIds: [] as number[],
            statusIds: [] as number[],
            commentIds: [] as number[],
            period: null as Period | null,
        })

        const data_state = reactive({
            cities: {
                is_loading: false,
                is_failed: false,
                list: [] as CityT[],
            },
            statuses: {
                is_loading: false,
                is_failed: false,
                list: [] as TripStatusTMS[],
            },
            comments: {
                is_loading: false,
                is_failed: false,
                list: [] as CommentTMS[],
            },
            transport_types: {
                is_loading: false,
                is_failed: false,
                list: [] as TransportTypeTMS[],
            },
        })

        // Города

        const loadCities = async () => {
            try {
                data_state.cities.is_loading = true

                const response = await locationAPI.getCities()
                data_state.cities.list = response.data.data

                data_state.cities.is_failed = false
            } catch (error) {
                data_state.cities.is_failed = true
                console.error(error)
            } finally {
                data_state.cities.is_loading = false
            }
        }

        // Статусы

        const loadStatuses = async () => {
            try {
                data_state.statuses.is_loading = true

                const response = await tmsAPI.getStatuses()
                data_state.statuses.list = response.data.data.filter(
                    (status) => !STATUSES_EXCLUDED.includes(status.id)
                )

                data_state.statuses.is_failed = false
            } catch (error) {
                data_state.statuses.is_failed = true
                console.error(error)
            } finally {
                data_state.statuses.is_loading = false
            }
        }

        // Комментарии

        const loadComments = async () => {
            try {
                data_state.comments.is_loading = true

                const response = await tmsAPI.getTtnComments()
                data_state.comments.list = response.data.data

                data_state.comments.is_failed = false
            } catch (error) {
                data_state.comments.is_failed = true
                console.error(error)
            } finally {
                data_state.comments.is_loading = false
            }
        }

        // Транспортные типы

        const loadTransportTypes = async () => {
            try {
                data_state.transport_types.is_loading = true

                const response = await tmsAPI.getTransportTypes()
                data_state.transport_types.list = response.data.data

                data_state.transport_types.is_failed = false
            } catch (error) {
                data_state.transport_types.is_failed = true
                console.error(error)
            } finally {
                data_state.transport_types.is_loading = false
            }
        }

        //

        const onFiltersChanged = () => {
            if (props.disabled || props.loading) return
            emit('submit', { ...state })
        }

        const onFiltersReset = () => {
            state.ttnNumber = null
            state.tripNumber = null
            state.fromCityId = null
            state.toCityId = null
            state.dateFrom = null
            state.dateTo = null
            state.transportTypeIds = []
            state.statusIds = []
            state.commentIds = []
            state.period = null

            emit('reset')
        }

        const manageToday = () => {
            state.dateFrom = getToday()
            state.dateTo = getToday()
        }

        const manageYesterday = () => {
            state.dateFrom = getYesterday()
            state.dateTo = getYesterday()
        }

        const manageThisWeek = () => {
            const { monday, sunday } = getThisWeek()

            state.dateFrom = monday
            state.dateTo = sunday
        }

        const managePreviousWeek = () => {
            const { previousMonday, previousSunday } = getPreviousWeek()

            state.dateFrom = previousMonday
            state.dateTo = previousSunday
        }

        const manageThisMonth = () => {
            const { firstDay, lastDay } = getThisMonth()

            state.dateFrom = firstDay
            state.dateTo = lastDay
        }

        const onPeriodChange = (period_id: Period['id'] | null) => {
            if (period_id) {
                if (period_id === 'today') {
                    manageToday()
                    return
                }

                if (period_id === 'yesterday') {
                    manageYesterday()
                    return
                }

                if (period_id === 'this_week') {
                    manageThisWeek()
                    return
                }

                if (period_id === 'previous_week') {
                    managePreviousWeek()
                    return
                }

                if (period_id === 'this_month') {
                    manageThisMonth()
                    return
                }
            }

            state.dateFrom = null
            state.dateTo = null
        }

        //

        onMounted(() => {
            loadCities()
            loadStatuses()
            loadComments()
            loadTransportTypes()
        })

        //

        return {
            state,
            data_state,
            //
            onFiltersChanged,
            onFiltersReset,
            //
            loadCities,
            loadStatuses,
            loadComments,
            loadTransportTypes,
            //
            onPeriodChange,
            PERIODS,
        }
    },
})
